import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import DashboardLayout from '../../components/DashboardLayout';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Search, Trash2, Edit, Plus, CheckCircle, XCircle, AlertCircle } from 'lucide-react';
import { Switch } from '../../components/ui/switch';
import UserModal from '../../components/admin/UserModal.jsx';
import api from '../../lib/api';
import { toast } from 'sonner';
import { DestructiveActionDialog } from '../../lib/DestructiveActionDialog';

const AdminUsers = () => {
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);

  useEffect(() => {
    loadUsers();
    loadPartners();
  }, []);

  const loadUsers = async () => {
    try {
      const response = await api.get('/users');
      setUsers(response.data);
    } catch (error) {
      console.error('Error loading users:', error);
      if (error.response?.status === 403) {
        console.log('Accès non autorisé');
      }
    } finally {
      setLoading(false);
    }
  };

  const loadPartners = async () => {
    try {
      const response = await api.get('/partners?is_active=true');
      // Tri alphabétique A→Z
      const sortedPartners = (response.data || [])
        .slice()
        .sort((a, b) => {
          const nameA = (a.nom || a.name || '').toLowerCase().trim();
          const nameB = (b.nom || b.name || '').toLowerCase().trim();
          return nameA.localeCompare(nameB, 'fr', { sensitivity: 'base' });
        });
      setPartners(sortedPartners);
    } catch (error) {
      console.error('Error loading partners:', error);
    }
  };

  const getPartnerName = (partnerId) => {
    if (!partnerId) return '-';
    const partner = partners.find(p => p.id === partnerId);
    return partner ? partner.nom : partnerId;
  };

  const [deleteTarget, setDeleteTarget] = useState(null);

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await api.delete(`/users/${deleteTarget.id}`);
      toast.success('Utilisateur supprimé');
      setDeleteTarget(null);
      loadUsers();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const handleValidate = async (id) => {
    try {
      await api.patch(`/users/${id}/validate`);
      toast.success('Utilisateur validé avec succès');
      loadUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la validation');
    }
  };

  const handleToggleActive = async (user) => {
    try {
      await api.patch(`/users/${user.id}/activate`, {
        is_active: !user.is_active
      });
      toast.success(user.is_active ? 'Utilisateur désactivé' : 'Utilisateur activé');
      loadUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la modification');
    }
  };

  const handleCreateUser = () => {
    setSelectedUser(null);
    setModalOpen(true);
  };

  const handleEditUser = (user) => {
    // Redirect to edit page instead of modal
    navigate(`/dashboard/admin/utilisateurs/${user.id}`);
  };

  const handleSaveUser = async (userData) => {
    try {
      if (selectedUser) {
        // Update existing user
        await api.put(`/users/${selectedUser.id}`, userData);
        toast.success('Utilisateur modifié avec succès');
      } else {
        // Create new user
        await api.post('/admin/users', userData);
        toast.success('Utilisateur créé avec succès');
      }
      loadUsers();
      setModalOpen(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'enregistrement');
      throw error;
    }
  };

  const filteredUsers = users.filter(user => {
    const searchLower = searchQuery.toLowerCase();
    return (
      user.pseudo?.toLowerCase().includes(searchLower) ||
      user.email?.toLowerCase().includes(searchLower) ||
      user.role?.toLowerCase().includes(searchLower)
    );
  });

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <div>
          <h1 className="text-3xl font-heading font-bold" data-testid="admin-users-title">
            Gestion des Utilisateurs
          </h1>
          <p className="text-muted-foreground mt-2">
            Consultez et gérez les comptes magasin inscrits
          </p>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="bg-white rounded-lg border p-6">
            <div className="text-sm text-muted-foreground mb-1">Total Utilisateurs</div>
            <div className="text-3xl font-bold" data-testid="total-users-count">{users.length}</div>
          </div>
          <div className="bg-white rounded-lg border p-6">
            <div className="text-sm text-muted-foreground mb-1">Magasins</div>
            <div className="text-3xl font-bold">{users.filter(u => u.role === 'retailer').length}</div>
          </div>
          <div className="bg-white rounded-lg border p-6">
            <div className="text-sm text-muted-foreground mb-1">Partenaires</div>
            <div className="text-3xl font-bold">{users.filter(u => u.role === 'partenaire').length}</div>
          </div>
          <div className="bg-white rounded-lg border p-6">
            <div className="text-sm text-muted-foreground mb-1">Admins</div>
            <div className="text-3xl font-bold">{users.filter(u => u.role === 'admin').length}</div>
          </div>
        </div>

        {/* Search & Actions */}
        <div className="flex gap-2">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Rechercher un utilisateur..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
              data-testid="search-users-input"
            />
          </div>
          <Button onClick={handleCreateUser} data-testid="create-user-button">
            <Plus className="h-4 w-4 mr-2" />
            Créer un utilisateur
          </Button>
        </div>

        {/* Table */}
        <div className="bg-white rounded-lg border">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Pseudo</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Rôle</TableHead>
                  <TableHead>Partner</TableHead>
                  <TableHead>Statut</TableHead>
                  <TableHead>Actif</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredUsers.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center py-8 text-muted-foreground">
                      Aucun utilisateur trouvé
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredUsers.map((user) => (
                    <TableRow key={user.id} data-testid={`user-row-${user.id}`}>
                      <TableCell>
                        <div className="font-medium">{user.pseudo}</div>
                      </TableCell>
                      <TableCell>
                        <div className="text-sm">{user.email}</div>
                      </TableCell>
                      <TableCell>
                        <span className="px-2 py-1 text-xs rounded-md bg-secondary capitalize">
                          {user.role === 'retailer' ? 'Magasin' : user.role === 'partenaire' ? 'Partenaire' : 'Admin'}
                        </span>
                      </TableCell>
                      <TableCell>
                        <div className="text-sm text-muted-foreground">
                          {getPartnerName(user.partner_id)}
                        </div>
                      </TableCell>
                      <TableCell>
                        {(() => {
                          const status = user.account_status;
                          if (status === 'email_pending') return (
                            <div className="flex items-center gap-1 text-gray-500">
                              <AlertCircle className="h-4 w-4" />
                              <span className="text-xs">Email non vérifié</span>
                            </div>
                          );
                          if (status === 'a_completer') return (
                            <div className="flex items-center gap-1 text-orange-600">
                              <AlertCircle className="h-4 w-4" />
                              <span className="text-xs">Profil incomplet</span>
                            </div>
                          );
                          if (status === 'a_valider') return (
                            <div className="flex items-center gap-1 text-blue-600">
                              <AlertCircle className="h-4 w-4" />
                              <span className="text-xs">À valider</span>
                            </div>
                          );
                          if (status === 'valide' || user.is_validated) return (
                            <div className="flex items-center gap-1 text-green-600">
                              <CheckCircle className="h-4 w-4" />
                              <span className="text-xs">Validé</span>
                            </div>
                          );
                          if (status === 'refuse') return (
                            <div className="flex items-center gap-1 text-red-600">
                              <AlertCircle className="h-4 w-4" />
                              <span className="text-xs">Refusé</span>
                            </div>
                          );
                          return <span className="text-xs text-muted-foreground">-</span>;
                        })()}
                      </TableCell>
                      <TableCell>
                        <Switch
                          checked={user.is_active}
                          onCheckedChange={() => handleToggleActive(user)}
                          data-testid={`toggle-active-${user.id}`}
                        />
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-2">
                          {user.role === 'retailer' && user.account_status === 'a_valider' && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleValidate(user.id)}
                              data-testid={`validate-user-${user.id}`}
                              className="text-green-600 hover:text-green-700"
                              title="Valider ce compte"
                            >
                              <CheckCircle className="h-4 w-4" />
                            </Button>
                          )}
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleEditUser(user)}
                            data-testid={`edit-user-${user.id}`}
                          >
                            <Edit className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setDeleteTarget(user)}
                            data-testid={`delete-user-${user.id}`}
                          >
                            <Trash2 className="h-4 w-4 text-red-500" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          )}
        </div>

        {/* User Modal */}
        <UserModal
          open={modalOpen}
          onClose={() => setModalOpen(false)}
          onSave={handleSaveUser}
          user={selectedUser}
        />

        <DestructiveActionDialog
          open={!!deleteTarget}
          onOpenChange={() => setDeleteTarget(null)}
          title="Supprimer l'utilisateur"
          itemName={deleteTarget?.pseudo || deleteTarget?.name || deleteTarget?.email || ''}
          description="Le compte utilisateur sera définitivement supprimé."
          onConfirm={handleDelete}
        />
      </div>
    </DashboardLayout>
  );
};

export default AdminUsers;
